import { EmbedObject, ParsedBody, Token, EmbedKind, YouTubeEmbed, MapEmbed, ExternalEmbed, DealEmbed } from './types';

function parseAttributes(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const attrPattern = /([\w:-]+)\s*=\s*"([^"]*)"|([\w:-]+)\s*=\s*'([^']*)'/g;
  let match: RegExpExecArray | null;
  while ((match = attrPattern.exec(attrString)) !== null) {
    const key = (match[1] || match[3] || '').toLowerCase();
    const value = match[2] || match[4] || '';
    if (key) {
      attrs[key] = value.trim();
    }
  }
  return attrs;
}

function extractYoutubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      return u.pathname.replace('/', '') || null;
    }
    if (u.searchParams.get('v')) {
      return u.searchParams.get('v');
    }
    const pathParts = u.pathname.split('/').filter(Boolean);
    if (pathParts[0] === 'embed' || pathParts[0] === 'shorts' || pathParts[0] === 'watch') {
      return pathParts[1] || null;
    }
    return pathParts[0] || null;
  } catch {
    return null;
  }
}

function buildYoutubeEmbed(id: string, videoId: string, raw: string): YouTubeEmbed {
  return {
    id,
    kind: 'youtube',
    raw,
    videoId,
    url: `https://www.youtube.com/embed/${videoId}`
  };
}

function buildMapEmbed(id: string, raw: string, url: string): MapEmbed {
  let query = url;
  try {
    const u = new URL(url);
    const searchQuery = u.searchParams.get('q') || u.searchParams.get('query');
    if (searchQuery) {
      query = searchQuery;
    } else {
      const segments = u.pathname.split('/').filter(Boolean);
      const placeIndex = segments.findIndex((seg) => seg.toLowerCase() === 'place');
      if (placeIndex >= 0 && segments[placeIndex + 1]) {
        try {
          query = decodeURIComponent(segments[placeIndex + 1].replace(/\+/g, ' '));
        } catch {
          query = segments[placeIndex + 1];
        }
      } else {
        query = u.toString();
      }
    }
  } catch {
    query = url;
  }
  return {
    id,
    kind: 'map',
    raw,
    query,
    url
  };
}

function buildExternalEmbed(id: string, raw: string, url: string, title?: string): ExternalEmbed {
  return {
    id,
    kind: 'external',
    raw,
    url,
    title
  };
}

function buildDealEmbed(
  id: string,
  raw: string,
  link: string,
  price: string,
  title?: string,
  image?: string
): DealEmbed {
  return {
    id,
    kind: 'deal',
    raw,
    link,
    price,
    title,
    image
  };
}

function normalizeKind(kind: string | undefined): EmbedKind | null {
  if (!kind) return null;
  const lower = kind.toLowerCase();
  if (lower === 'youtube' || lower === 'map' || lower === 'external' || lower === 'deal') {
    return lower as EmbedKind;
  }
  return null;
}

export function parseEmbeds(html: string): ParsedBody {
  const tokens: Token[] = [];
  const embeds: EmbedObject[] = [];
  if (!html) return { tokens: [], embeds: [] };

  const pattern = /<(embed|deal)([^>]*)\/>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(html)) !== null) {
    const [fullMatch, tagName, attrString] = match;
    const startIndex = match.index;

    if (startIndex > lastIndex) {
      tokens.push({ type: 'text', html: html.slice(lastIndex, startIndex) });
    }

    const attrs = parseAttributes(attrString || '');
    const kind = tagName.toLowerCase() === 'deal' ? 'deal' : normalizeKind(attrs['type']);
    const embedId = `embed-${embeds.length}`;

    let embedObj: EmbedObject | null = null;

    if (kind === 'youtube' && attrs.url) {
      const videoId = extractYoutubeId(attrs.url);
      if (videoId) {
        embedObj = buildYoutubeEmbed(embedId, videoId, fullMatch);
      }
    } else if (kind === 'map' && attrs.url) {
      embedObj = buildMapEmbed(embedId, fullMatch, attrs.url);
    } else if (kind === 'external' && attrs.url) {
      embedObj = buildExternalEmbed(embedId, fullMatch, attrs.url, attrs.title);
    } else if (kind === 'deal' && (attrs.link || attrs.url) && attrs.price) {
      const link = attrs.link || attrs.url;
      embedObj = buildDealEmbed(embedId, fullMatch, link, attrs.price, attrs.title, attrs.image);
    }

    if (embedObj) {
      embeds.push(embedObj);
      tokens.push({ type: 'embed', embedId: embedObj.id });
    } else {
      tokens.push({ type: 'text', html: fullMatch });
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < html.length) {
    tokens.push({ type: 'text', html: html.slice(lastIndex) });
  }

  return { tokens, embeds };
}
